import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react'
import { Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import { api } from './api'
import { wrapData, unwrapData, generateId, parseOTPAuthURI, generateTOTP, exportVault } from './crypto'
import { isBiometricsAvailable, hasEnrolledBiometrics, registerBiometrics, unlockWithBiometrics, clearBiometrics, prfSupported, getEnrolledUser } from './biometrics'
import { AuroraBackground, SpotlightCard } from './effects'

const AuthContext = createContext(null)

// Password input with eye toggle — shared by every password field
function PasswordField({ value, onChange, className = '', ...props }) {
  const [show, setShow] = useState(false)
  return (
    <div className={`relative ${className}`}>
      <input
        {...props}
        type={show ? 'text' : 'password'}
        className="input pr-11 w-full"
        value={value}
        onChange={onChange}
      />
      <button
        type="button"
        aria-label={show ? 'Hide password' : 'Show password'}
        onClick={() => setShow(s => !s)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-vault-muted hover:text-vault-accent"
        tabIndex={-1}
      >
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [masterPw, setMasterPw] = useState(() => sessionStorage.getItem('vault_unlock') || '')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.me().then(r => {
      setUser({ id: r.user_id, username: r.username })
      if (r.username) localStorage.setItem('vault_bio_user', r.username)
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })
  }, [])

  const login = async (username, password) => {
    await api.login(username, password)
    const me = await api.me()
    const uname = me.username || username
    setUser({ id: me.user_id, username: uname })
    if (uname) localStorage.setItem('vault_bio_user', uname)
    setMasterPw(password)
    sessionStorage.setItem('vault_unlock', password)
  }

  const register = async (username, password) => {
    await api.register(username, password)
    await login(username, password)
  }

  const logout = async () => {
    await api.logout()
    setUser(null)
    setMasterPw('')
    sessionStorage.removeItem('vault_unlock')
  }

  // Biometric (fingerprint) unlock: restores master password into session
  // without re-authenticating to the server. User is already logged in
  // (server cookie); only the local encryption key was cleared on lock.
  const unlockSession = useCallback((pw) => {
    setMasterPw(pw)
    sessionStorage.setItem('vault_unlock', pw)
  }, [])

  // ponytail: auto-lock clears master password after 5 min idle; bump LOCK_MS if too aggressive
  const lockRef = useRef(null)
  const doLock = useCallback(() => {
    setMasterPw('')
    sessionStorage.removeItem('vault_unlock')
  }, [])
  useEffect(() => {
    if (!user || !masterPw) return
    const reset = () => {
      clearTimeout(lockRef.current)
      lockRef.current = setTimeout(doLock, 5 * 60 * 1000)
    }
    reset()
    const evts = ['mousedown', 'keydown', 'touchstart']
    evts.forEach(e => window.addEventListener(e, reset, { passive: true }))
    return () => {
      clearTimeout(lockRef.current)
      evts.forEach(e => window.removeEventListener(e, reset))
    }
  }, [user, masterPw, doLock])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-vault-muted">Loading...</div>
      </div>
    )
  }

  return (
    <AuthContext.Provider value={{ user, masterPw, login, register, logout, unlockSession }}>
      {children}
    </AuthContext.Provider>
  )
}

function SetupPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const { register } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    if (password.length < 12) {
      setError('Password must be at least 12 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    try {
      await register(username, password)
      navigate('/vault')
    } catch (err) {
      setError(err.message)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <AuroraBackground />
      <SpotlightCard className="card max-w-md w-full">
        <h1 className="text-2xl font-bold mb-6 text-center">🔐 Vault Authenticator</h1>
        <p className="text-vault-muted text-sm mb-6 text-center">
          Create your account. Your master password is your encryption key.
        </p>
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-vault-muted mb-1">Username</label>
            <input
              type="text"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Master Password (min 12 chars)</label>
            <PasswordField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={12}
              required
            />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Confirm Password</label>
            <PasswordField
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              minLength={12}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary w-full">
            Create Account
          </button>
        </form>
      </SpotlightCard>
    </div>
  )
}

function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [bioBusy, setBioBusy] = useState(false)
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const hasBio = hasEnrolledBiometrics()

  useEffect(() => {
    const savedUser = getEnrolledUser()
    if (savedUser && !username) {
      setUsername(savedUser)
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    try {
      await login(username, password)
      navigate('/vault')
    } catch (err) {
      setError(err.message)
    }
  }

  const handleBioLogin = async () => {
    setError('')
    setBioBusy(true)
    try {
      const res = await unlockWithBiometrics()
      const pw = typeof res === 'object' ? res.masterPw : res
      const u = (typeof res === 'object' && res.username) || username || getEnrolledUser()
      if (!u) {
        setError('Please enter your Username first')
        setBioBusy(false)
        return
      }
      await login(u, pw)
      navigate('/vault')
    } catch (err) {
      setError(err.message || 'Fingerprint login failed')
    } finally {
      setBioBusy(false)
    }
  }

  if (user) {
    navigate('/vault')
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <AuroraBackground />
      <SpotlightCard className="card max-w-md w-full">
        <h1 className="text-2xl font-bold mb-6 text-center">🔐 Vault Login</h1>
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {hasBio && (
          <div className="mb-6">
            <button
              type="button"
              onClick={handleBioLogin}
              disabled={bioBusy}
              className="btn btn-ghost w-full py-3 border-2 border-vault-accent/50 text-vault-accent hover:bg-vault-accent/10 flex items-center justify-center gap-3 font-semibold text-base shadow-lg shadow-vault-accent/5"
            >
              <span className="text-2xl">👆</span>
              <span>{bioBusy ? 'Verifying Fingerprint...' : `Login with Fingerprint${getEnrolledUser() ? ` (${getEnrolledUser()})` : ''}`}</span>
            </button>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-vault-border" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-vault-card px-2 text-vault-muted">Or enter password</span></div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-vault-muted mb-1">Username</label>
            <input
              type="text"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Master Password</label>
            <PasswordField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary w-full">
            Unlock Vault
          </button>
          <Link to="/setup" className="block text-center text-vault-muted text-sm hover:text-vault-accent">
            Don't have an account? Create one
          </Link>
        </form>
      </SpotlightCard>
    </div>
  )
}

// Generate color from string (deterministic)
function colorFromString(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  const hue = Math.abs(h) % 360
  return `hsl(${hue}, 65%, 35%)`
}

// Initials (max 2 chars) from string
function initialsFrom(s) {
  if (!s) return '?'
  const parts = s.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return s.slice(0, 2).toUpperCase()
}

function VendorAvatar({ vendor, size = 'md' }) {
  const text = vendor || '?'
  const sizeCls = size === 'lg' ? 'w-12 h-12 text-lg' : 'w-10 h-10 text-base'
  return (
    <div
      className={`${sizeCls} rounded-lg flex items-center justify-center text-white font-bold shrink-0`}
      style={{ backgroundColor: colorFromString(text) }}
    >
      {initialsFrom(text)}
    </div>
  )
}

// Single tile with live TOTP
function VaultTile({ entry, onClick, masterPw }) {
  const [code, setCode] = useState('')
  const [timeLeft, setTimeLeft] = useState(30)
  const [decrypted, setDecrypted] = useState(null)
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)

  // Decrypt once
  useEffect(() => {
    let cancelled = false
    if (!masterPw) {
      setDecrypted(null)
      setCode('')
      setRevealed(false)
      return
    }
    api.getBlob(entry.id).then(async (full) => {
      if (cancelled) return
      try {
        const data = await unwrapData(full, masterPw)
        if (!cancelled) setDecrypted(data)
      } catch {}
    }).catch(() => {})
    return () => { cancelled = true }
  }, [entry.id, masterPw])

  // Live TOTP
  useEffect(() => {
    if (!decrypted?.totpSecret) {
      setCode('')
      setTimeLeft(30)
      return
    }
    let interval
    const tick = async () => {
      const c = await generateTOTP(decrypted.totpSecret)
      if (c) setCode(c)
      setTimeLeft(30 - (Math.floor(Date.now() / 1000) % 30))
    }
    tick()
    interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [decrypted])

  const toggleRevealOrCopy = async (e) => {
    e.stopPropagation()
    if (!code) return
    if (!revealed) {
      setRevealed(true)
      // ponytail: auto-mask again after 15 seconds to prevent shoulder surfing
      setTimeout(() => setRevealed(false), 15000)
    } else {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  return (
    <SpotlightCard
      onClick={onClick}
      className="bg-vault-card rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:border-vault-accent border border-transparent transition-colors"
    >
      <VendorAvatar vendor={entry.vendor || entry.label} />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{entry.label}</div>
        <div className="text-xs text-vault-muted truncate">
          {entry.vendor || 'Uncategorized'}
        </div>
      </div>
      <div className="text-right shrink-0" onClick={toggleRevealOrCopy} role="button" aria-label="Toggle or copy TOTP code">
        {decrypted?.totpSecret ? (
          <>
            <div className="flex items-center justify-end gap-2">
              <div className="text-2xl font-mono tracking-wider text-vault-accent select-none">
                {revealed ? (code ? code.match(/.{1,3}/g)?.join(' ') : '••• •••') : '••• •••'}
              </div>
              <span className="text-sm text-vault-muted">
                {copied ? '✓' : revealed ? '📋' : '👁️'}
              </span>
            </div>
            <div className="flex items-center justify-end gap-2 mt-1">
              <span className="text-[10px] text-vault-muted">
                {copied ? 'Copied!' : revealed ? 'tap copy' : 'tap reveal'}
              </span>
              <div className="w-12 bg-gray-700 rounded-full h-1">
                <div
                  className={`h-1 rounded-full transition-all duration-1000 ${timeLeft <= 5 ? 'bg-vault-danger' : 'bg-vault-accent'}`}
                  style={{ width: `${(timeLeft / 30) * 100}%` }}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="text-vault-muted text-sm">tap to view</div>
        )}
      </div>
    </SpotlightCard>
  )
}

function SettingsPage() {
  const { user, logout, masterPw } = useAuth()
  const navigate = useNavigate()
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [backupMsg, setBackupMsg] = useState('')
  const [backupErr, setBackupErr] = useState('')
  const [exporting, setExporting] = useState(false)
  const [trash, setTrash] = useState([])
  const [hasBio, setHasBio] = useState(false)
  const [bioSupported, setBioSupported] = useState(false)
  const [bioPrf, setBioPrf] = useState(false)
  const [bioMsg, setBioMsg] = useState('')
  const [bioErr, setBioErr] = useState('')
  const importInputRef = useRef(null)

  useEffect(() => {
    isBiometricsAvailable().then(setBioSupported)
    prfSupported().then(setBioPrf)
    setHasBio(hasEnrolledBiometrics())
  }, [])

  const handleEnableBio = async () => {
    setBioMsg('')
    setBioErr('')
    if (!masterPw) {
      setBioErr('Unlock vault with master password first before enabling fingerprint')
      return
    }
    try {
      await registerBiometrics(user?.username || 'user', masterPw)
      setHasBio(true)
      setBioMsg('Fingerprint registered successfully on this device!')
    } catch (e2) {
      setBioErr(e2.message)
    }
  }

  const handleDisableBio = () => {
    clearBiometrics()
    setHasBio(false)
    setBioMsg('Fingerprint disabled on this device.')
    setBioErr('')
  }

  const loadTrash = useCallback(async () => {
    try {
      const data = await api.getTrash()
      setTrash(data)
    } catch {}
  }, [])

  useEffect(() => {
    if (!user) navigate('/login')
    loadTrash()
  }, [user])

  const handleExport = async () => {
    setBackupMsg('')
    setBackupErr('')
    if (!masterPw) {
      setBackupErr('Vault is locked — unlock from the vault list first')
      return
    }
    try {
      setExporting(true)
      const data = await api.exportVault()
      const wrapped = await exportVault(data.entries, masterPw)
      const blob = new Blob([JSON.stringify(wrapped)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vault-backup-${new Date().toISOString().slice(0, 10)}.json`
      a.click()
      URL.revokeObjectURL(url)
      setBackupMsg(`Backup downloaded — ${data.entries.length} entries encrypted`)
    } catch (e2) {
      setBackupErr(e2.message)
    } finally {
      setExporting(false)
    }
  }

  const handleImport = () => {
    setBackupMsg('')
    setBackupErr('')
    if (!masterPw) {
      setBackupErr('Vault is locked — unlock from the vault list first')
      return
    }
    importInputRef.current?.click()
  }

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setBackupMsg('')
    setBackupErr('')
    try {
      const text = await file.text()
      const wrapped = JSON.parse(text)
      const data = await unwrapData(wrapped, masterPw)
      if (data?.version !== 1 || !Array.isArray(data?.entries)) throw new Error('Not a valid vault backup file')
      const res = await api.importVault(data.entries)
      setBackupMsg(`Imported ${res.imported} entries`)
    } catch (e2) {
      setBackupErr('Import failed: ' + e2.message)
    }
  }

  const handleRestore = async (id) => {
    try {
      await api.restoreBlob(id)
      setTrash(t => t.filter(i => i.id !== id))
    } catch (e2) {
      setBackupErr(e2.message)
    }
  }

  const handlePermanentDelete = async (id) => {
    if (!confirm('Delete this item forever? This cannot be undone.')) return
    try {
      await api.permanentDeleteBlob(id)
      setTrash(t => t.filter(i => i.id !== id))
    } catch (e2) {
      setBackupErr(e2.message)
    }
  }

  useEffect(() => {
    if (!user) navigate('/login')
    loadTrash()
  }, [user])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErr('')
    setMsg('')
    if (newPw.length < 12) {
      setErr('New password must be at least 12 characters')
      return
    }
    if (newPw !== confirmPw) {
      setErr('New passwords do not match')
      return
    }
    if (newPw === currentPw) {
      setErr('New password must be different')
      return
    }
    try {
      await api.changePassword(currentPw, newPw)
      setMsg('Login password changed.')
      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
    } catch (e2) {
      setErr(e2.message)
    }
  }

  return (
    <div className="min-h-screen">
      <AuroraBackground />
      <header className="bg-vault-card border-b border-gray-800 p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/vault')} className="btn btn-ghost">← Back</button>
          <h1 className="text-xl font-bold flex-1">Settings</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto p-4 space-y-4">
        <SpotlightCard className="card">
          <h3 className="text-lg font-semibold mb-1">Login Password</h3>
          <p className="text-vault-muted text-sm mb-4">
            Used to sign in. Does NOT change your master password — encrypted entries stay readable.
          </p>
          {msg && <div className="bg-green-900/30 border border-green-700 text-green-400 px-4 py-2 rounded-lg mb-4 text-sm">{msg}</div>}
          {err && <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">{err}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-vault-muted mb-1">Current login password</label>
              <PasswordField value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm text-vault-muted mb-1">New login password (min 12 chars)</label>
              <PasswordField value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={12} required />
            </div>
            <div>
              <label className="block text-sm text-vault-muted mb-1">Confirm new password</label>
              <PasswordField value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} minLength={12} required />
            </div>
            <button type="submit" className="btn btn-primary w-full">Change Password</button>
          </form>
        </SpotlightCard>
        <SpotlightCard className="card">
          <h3 className="text-lg font-semibold mb-1">👆 Fingerprint Unlock</h3>
          <p className="text-vault-muted text-sm mb-2">
            Unlock the vault without typing your master password — just tap your fingerprint.
          </p>
          <div className="text-vault-muted text-sm mb-3">
            {bioSupported
              ? <span className="text-green-400">✓ Device supports fingerprint</span>
              : <span className="text-yellow-400">⚠ Device fingerprint support unverified — tap "Enable" to try</span>}
            {!bioPrf && bioSupported && (
              <div className="text-yellow-400 mt-1">PRF capability check inconclusive — will verify during enrollment</div>
            )}
          </div>
          {bioErr && <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-3 text-sm">{bioErr}</div>}
          {bioMsg && <div className="bg-green-900/30 border border-green-700 text-green-400 px-4 py-2 rounded-lg mb-3 text-sm">{bioMsg}</div>}
          <div className="flex gap-2">
            <button
              onClick={hasBio ? handleDisableBio : handleEnableBio}
              className={`${hasBio ? 'btn btn-ghost' : 'btn btn-primary'} flex-1`}
            >
              {hasBio ? 'Remove Fingerprint' : '👆 Enable Fingerprint'}
            </button>
          </div>
        </SpotlightCard>

        <SpotlightCard className="card">
          <h3 className="text-lg font-semibold mb-1">Backup & Export</h3>
          <p className="text-vault-muted text-sm mb-4">
            Download all entries as an encrypted JSON backup. Protected by your master password — keep it safe.
          </p>
          {backupMsg && <div className="bg-green-900/30 border border-green-700 text-green-400 px-4 py-2 rounded-lg mb-4 text-sm">{backupMsg}</div>}
          {backupErr && <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">{backupErr}</div>}
          <button onClick={handleExport} disabled={exporting} className="btn btn-primary w-full mb-2">
            {exporting ? 'Encrypting…' : '📥 Download Encrypted Backup'}
          </button>
          <button onClick={handleImport} disabled={exporting} className="btn btn-ghost w-full">
            📤 Import Backup
          </button>
          <input ref={importInputRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImportFile} />
        </SpotlightCard>

        <SpotlightCard className="card">
          <h3 className="text-lg font-semibold mb-1">🗑️ Trash</h3>
          <p className="text-vault-muted text-sm mb-4">
            Deleted items are kept here for recovery. Restore or permanently delete them below.
          </p>
          {trash.length === 0 ? (
            <p className="text-vault-muted text-sm">Trash is empty.</p>
          ) : (
            <ul className="space-y-2">
              {trash.map(item => (
                <li key={item.id} className="flex items-center gap-2">
                  <span className="flex-1 min-w-0 truncate">{item.label}</span>
                  <button onClick={() => handleRestore(item.id)} className="btn btn-ghost text-xs">Restore</button>
                  <button onClick={() => handlePermanentDelete(item.id)} className="btn btn-danger text-xs">Delete Forever</button>
                </li>
              ))}
            </ul>
          )}
        </SpotlightCard>

        <SpotlightCard className="card">
          <h3 className="text-lg font-semibold mb-1">Account</h3>
          <p className="text-vault-muted text-sm mb-4">Signed in as user <code className="text-vault-accent">{user?.id?.slice(0, 8)}</code></p>
          <button onClick={logout} className="btn btn-ghost w-full">Logout</button>
        </SpotlightCard>
      </main>
    </div>
  )
}

function VaultListPage() {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [addFromQR, setAddFromQR] = useState(null)
  const [unlockPw, setUnlockPw] = useState('')
  const [unlockError, setUnlockError] = useState('')
  const [bioReady, setBioReady] = useState(false)
  const [bioBusy, setBioBusy] = useState(false)
  const { user, masterPw, logout, unlockSession } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    setBioReady(hasEnrolledBiometrics())
  }, [])

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    loadVault()
  }, [user])

  // Handle QR scan result from navigation state
  useEffect(() => {
    if (location.state?.qrData) {
      setAddFromQR(location.state.qrData)
      setShowAddModal(true)
      window.history.replaceState({}, '')
    }
  }, [location.state])

  const loadVault = async () => {
    try {
      const data = await api.listVault()
      setEntries(data)
    } catch (err) {
      console.error('Failed to load vault:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this entry?')) return
    try {
      await api.deleteBlob(id)
      setEntries(entries.filter(e => e.id !== id))
    } catch (err) {
      alert('Failed to delete: ' + err.message)
    }
  }

  // Re-prompt for master password (e.g. page refresh)
  const handleUnlock = async (e) => {
    e.preventDefault()
    setUnlockError('')
    try {
      // Verify by attempting to decrypt the first blob (if any)
      if (entries.length > 0) {
        const first = await api.getBlob(entries[0].id)
        await unwrapData(first, unlockPw)
      }
      // Use the AuthContext setter via a side channel: just reload page after setting in sessionStorage
      unlockSession(unlockPw)
      window.location.reload()
    } catch {
      setUnlockError('Wrong password')
    }
  }

  const handleBioUnlock = async () => {
    setUnlockError('')
    setBioBusy(true)
    try {
      const res = await unlockWithBiometrics()
      const pw = typeof res === 'object' ? res.masterPw : res
      unlockSession(pw)
      window.location.reload()
    } catch (err) {
      setUnlockError(err.message)
    } finally {
      setBioBusy(false)
    }
  }

  const filteredEntries = entries.filter(e =>
    e.label.toLowerCase().includes(search.toLowerCase()) ||
    (e.vendor || '').toLowerCase().includes(search.toLowerCase())
  )

  // Group by vendor (MS Authenticator style)
  const grouped = filteredEntries.reduce((acc, e) => {
    const key = e.vendor || 'Uncategorized'
    if (!acc[key]) acc[key] = []
    acc[key].push(e)
    return acc
  }, {})
  const groupNames = Object.keys(grouped).sort()

  // If no masterPw in memory (page refresh), show unlock prompt
  if (!masterPw) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">🔒 Unlock Vault</h2>
          <p className="text-sm text-vault-muted mb-4">
            Enter master password to decrypt and view TOTP codes.
          </p>
          {unlockError && (
            <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
              {unlockError}
            </div>
          )}
          {bioReady && (
            <button
              type="button"
              onClick={handleBioUnlock}
              disabled={bioBusy}
              className="btn btn-ghost w-full mb-3 border border-vault-accent/30 text-vault-accent"
            >
              {bioBusy ? '⏳ Waiting for fingerprint…' : '👆 Unlock with Fingerprint'}
            </button>
          )}
          <form onSubmit={handleUnlock} className="space-y-4">
            <PasswordField
              value={unlockPw}
              onChange={(e) => setUnlockPw(e.target.value)}
              placeholder="Master password"
              required
              autoFocus
            />
            <div className="flex gap-2">
              <button type="button" onClick={logout} className="btn btn-ghost flex-1">Logout</button>
              <button type="submit" className="btn btn-primary flex-1">Unlock</button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <AuroraBackground />
      <header className="bg-vault-card border-b border-gray-800 p-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Link to="/qr-scan" className="btn btn-ghost">📷 Scan QR</Link>
            <Link to="/settings" className="btn btn-ghost" aria-label="Settings">⚙️</Link>
            <button onClick={logout} className="btn btn-ghost">Logout</button>
          </div>
          <h1 className="text-lg font-bold">Vault</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        <input
          type="text"
          className="input"
          placeholder="Search by name or vendor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {!masterPw && !loading && filteredEntries.length > 0 && (
          <form onSubmit={handleUnlock} className="card space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-vault-muted">🔒 Vault locked — re-enter master password</span>
              <span className="flex-1" />
            </div>
            {bioReady && (
              <button
                type="button"
                onClick={handleBioUnlock}
                disabled={bioBusy}
                className="btn btn-ghost w-full border border-vault-accent/30 text-vault-accent"
              >
                {bioBusy ? '⏳ Waiting for fingerprint…' : '👆 Unlock with Fingerprint'}
              </button>
            )}
            <PasswordField
              placeholder="Master password"
              value={unlockPw}
              onChange={(e) => setUnlockPw(e.target.value)}
              required
              autoFocus
            />
            {unlockError && <div className="text-red-400 text-sm">{unlockError}</div>}
            <button type="submit" className="btn btn-primary w-full">Unlock</button>
          </form>
        )}

        <div className="flex justify-between items-center">
          <h2 className="text-sm uppercase tracking-wide text-vault-muted">
            {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} · {groupNames.length} {groupNames.length === 1 ? 'vendor' : 'vendors'}
          </h2>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            + Add
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-vault-muted">Loading...</div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-vault-muted">
            <p className="text-lg mb-2">No entries</p>
            <p className="text-sm">Tap "+ Add" or "📷 Scan QR" to start</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groupNames.map(vendor => (
              <div key={vendor}>
                <div className="sticky top-0 bg-vault-bg/95 backdrop-blur py-2 mb-2 flex items-center gap-2">
                  <VendorAvatar vendor={vendor} size="sm" />
                  <h3 className="text-sm font-semibold uppercase tracking-wide">
                    {vendor}
                  </h3>
                  <span className="text-xs text-vault-muted">({grouped[vendor].length})</span>
                </div>
                <div className="space-y-2">
                  {grouped[vendor].map(entry => (
                    <VaultTile
                      key={entry.id}
                      entry={entry}
                      masterPw={masterPw}
                      onClick={() => navigate(`/vault/${entry.id}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {showAddModal && (
        <AddEntryModal
          initialData={addFromQR}
          onClose={() => { setShowAddModal(false); setAddFromQR(null) }}
          onSaved={(entry) => {
            setEntries([entry, ...entries])
            setShowAddModal(false)
            setAddFromQR(null)
          }}
        />
      )}
    </div>
  )
}

function AddEntryModal({ onClose, onSaved, initialData = null }) {
  const [label, setLabel] = useState(initialData?.label || initialData?.issuer || '')
  const [vendor, setVendor] = useState(initialData?.issuer || '')
  const [username, setUsername] = useState(initialData?.username || '')
  const [password, setPassword] = useState(initialData?.password || '')
  const [totpSecret, setTotpSecret] = useState(initialData?.secret || '')
  const [notes, setNotes] = useState(initialData?.notes || '')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const { masterPw } = useAuth()

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!masterPw) {
      setError('Vault is locked — unlock from the vault list first')
      return
    }
    
    setSaving(true)
    try {
      const payload = { label, username, password, totpSecret, notes }
      const encrypted = await wrapData(payload, masterPw)
      
      const blob = {
        label: label || 'Untitled',
        vendor: vendor.trim(),
        ...encrypted
      }
      
      const result = await api.createBlob(blob)
      onSaved({ id: result.id, ...blob, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Add Vault Entry</h2>
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-vault-muted mb-1">Label</label>
            <input className="input" value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g., Google, GitHub" />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Vendor / Group (optional)</label>
            <input className="input" value={vendor} onChange={e => setVendor(e.target.value)} placeholder="e.g., Google, Vercel, AWS" />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Username</label>
            <input className="input" value={username} onChange={e => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Password</label>
            <PasswordField value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">TOTP Secret (base32)</label>
            <input className="input" value={totpSecret} onChange={e => setTotpSecret(e.target.value)} placeholder="JBSWY3DPEHPK3PXP" />
          </div>
          <div>
            <label className="block text-sm text-vault-muted mb-1">Notes</label>
            <textarea className="input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn btn-ghost flex-1">Cancel</button>
            <button type="submit" className="btn btn-primary flex-1" disabled={saving}>
              {saving ? 'Encrypting...' : 'Save Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function VaultEntryPage() {
  const { id } = useParams()
  const [entry, setEntry] = useState(null)
  const [decrypted, setDecrypted] = useState(null)
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState({})
  const [editVendor, setEditVendor] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [totpTime, setTotpTime] = useState(30)
  const { user, masterPw } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    loadEntry()
  }, [user, id])

  useEffect(() => {
    if (!decrypted?.totpSecret) return
    let interval
    const updateTOTP = async () => {
      try {
        const code = await generateTOTP(decrypted.totpSecret)
        if (code) setTotpCode(code)
        setTotpTime(30 - (Math.floor(Date.now() / 1000) % 30))
      } catch {
        setTotpCode('------')
      }
    }
    updateTOTP()
    interval = setInterval(updateTOTP, 1000)
    return () => clearInterval(interval)
  }, [decrypted])

  const loadEntry = async () => {
    try {
      const data = await api.getBlob(id)
      setEntry(data)
      setEditVendor(data.vendor || '')
    } catch (err) {
      setError('Failed to load entry')
    } finally {
      setLoading(false)
    }
  }

  // Auto-decrypt when session has master password; show unlock prompt only when locked
  useEffect(() => {
    if (!entry || !masterPw || decrypted) return
    unwrapData(entry, masterPw).then(data => {
      setDecrypted(data)
      setEditData(data)
    }).catch(() => {
      setError('Wrong master password — unlock again from vault list')
    })
  }, [entry, masterPw, decrypted])

  const handleSave = async (e) => {
    e.preventDefault()
    try {
      const encrypted = await wrapData(editData, masterPw)
      await api.updateBlob(id, { label: editData.label, vendor: editVendor, ...encrypted })
      setEntry({ ...entry, vendor: editVendor })
      setDecrypted(editData)
      setEditing(false)
    } catch (err) {
      setError('Failed to save: ' + err.message)
    }
  }

  const copyToClipboard = async (text, label) => {
    await navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
    if (label !== 'label') {
      setTimeout(() => navigator.clipboard.writeText(''), 30000)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-vault-muted">Loading...</div>
      </div>
    )
  }

  if (error && !entry) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card">
          <p className="text-red-400">{error}</p>
          <button onClick={() => navigate('/vault')} className="btn btn-ghost mt-4">
            Back to Vault
          </button>
        </div>
      </div>
    )
  }

  if (!decrypted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">🔒 Vault Locked</h2>
          {error && (
            <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}
          <p className="text-vault-muted text-sm mb-4">
            Enter your master password on the vault list to unlock entries.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => navigate('/vault')} className="btn btn-ghost flex-1">
              Back
            </button>
            <button type="button" onClick={() => window.location.reload()} className="btn btn-primary flex-1">
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <AuroraBackground />
      <header className="bg-vault-card border-b border-gray-800 p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/vault')} className="btn btn-ghost">
            ← Back
          </button>
          <h1 className="text-xl font-bold flex-1">{decrypted.label}</h1>
          <button onClick={() => setEditing(!editing)} className="btn btn-ghost">
            {editing ? 'Cancel' : '✏️ Edit'}
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        {decrypted.totpSecret && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Authenticator</h3>
              <div className="text-sm text-vault-muted">
                {totpTime}s
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-mono tracking-[0.3em] text-vault-accent mb-4">
                {totpCode.match(/.{1,3}/g)?.join(' ') || '--- ---'}
              </div>
              <div className="w-full bg-gray-700 rounded-full h-1.5 mb-4">
                <div
                  className="bg-vault-accent h-1.5 rounded-full transition-all duration-1000"
                  style={{ width: `${(totpTime / 30) * 100}%` }}
                />
              </div>
              <button
                onClick={() => copyToClipboard(totpCode, 'totp')}
                className="btn btn-primary"
              >
                {copied === 'totp' ? '✓ Copied!' : '📋 Copy Code'}
              </button>
            </div>
          </div>
        )}

        <div className="card space-y-4">
          <div>
            <label className="block text-sm text-vault-muted mb-1">Username</label>
            <div className="flex gap-2">
              {editing ? (
                <input
                  className="input flex-1"
                  value={editData.username}
                  onChange={e => setEditData({ ...editData, username: e.target.value })}
                />
              ) : (
                <div className="input flex-1 flex items-center justify-between bg-vault-bg">
                  <span>{decrypted.username || '—'}</span>
                  {decrypted.username && (
                    <button
                      onClick={() => copyToClipboard(decrypted.username, 'username')}
                      className="text-vault-muted hover:text-vault-accent text-sm"
                    >
                      {copied === 'username' ? '✓' : '📋'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm text-vault-muted mb-1">Password</label>
            <div className="flex gap-2">
              {editing ? (
                <PasswordField
                  className="flex-1"
                  value={editData.password}
                  onChange={e => setEditData({ ...editData, password: e.target.value })}
                />
              ) : (
                <div className="input flex-1 flex items-center justify-between bg-vault-bg">
                  <span className="font-mono break-all text-left">
                    {showPassword ? (decrypted.password || '—') : (decrypted.password ? '••••••••••' : '—')}
                  </span>
                  {decrypted.password && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setShowPassword(s => !s)}
                        className="text-vault-muted hover:text-vault-accent text-sm"
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                      <button
                        onClick={() => copyToClipboard(decrypted.password, 'password')}
                        className="text-vault-muted hover:text-vault-accent text-sm"
                      >
                        {copied === 'password' ? '✓ Copied!' : '📋'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {editing && (
            <div>
              <label className="block text-sm text-vault-muted mb-1">Vendor / Group</label>
              <input
                className="input"
                value={editVendor}
                onChange={e => setEditVendor(e.target.value)}
                placeholder="e.g., Google, Vercel, AWS"
              />
            </div>
          )}

          {editing && (
            <div>
              <label className="block text-sm text-vault-muted mb-1">TOTP Secret</label>
              <input
                className="input"
                value={editData.totpSecret}
                onChange={e => setEditData({ ...editData, totpSecret: e.target.value })}
                placeholder="JBSWY3DPEHPK3PXP"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-vault-muted mb-1">Notes</label>
            {editing ? (
              <textarea
                className="input"
                rows={3}
                value={editData.notes}
                onChange={e => setEditData({ ...editData, notes: e.target.value })}
              />
            ) : (
              <div className="input bg-vault-bg whitespace-pre-wrap">{decrypted.notes || '—'}</div>
            )}
          </div>

          {editing && (
            <div className="border-t border-gray-700 pt-4">
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditing(false)} className="btn btn-ghost flex-1">
                  Cancel
                </button>
                <button type="button" onClick={handleSave} className="btn btn-primary flex-1">
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center">
          <button
            onClick={async () => {
              if (confirm('Delete this entry permanently?')) {
                await api.deleteBlob(id)
                navigate('/vault')
              }
            }}
            className="btn btn-danger text-sm"
          >
            🗑️ Delete Entry
          </button>
        </div>
      </main>
    </div>
  )
}

function QRScannerPage() {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const fileInputRef = useRef(null)
  const scanningRef = useRef(false)
  const [active, setActive] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  const stopScanning = () => {
    scanningRef.current = false
    setActive(false)
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop())
    }
  }

  const decodeFromImageData = async (data, width, height) => {
    const jsQR = (await import('jsqr')).default
    return jsQR(data, width, height)
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError('')
    try {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.src = url
      await img.decode()
      const c = document.createElement('canvas')
      c.width = img.naturalWidth
      c.height = img.naturalHeight
      const ctx = c.getContext('2d')
      ctx.drawImage(img, 0, 0)
      const imageData = ctx.getImageData(0, 0, c.width, c.height)
      const code = await decodeFromImageData(imageData.data, imageData.width, imageData.height)
      URL.revokeObjectURL(url)
      if (code?.data?.startsWith('otpauth://')) {
        const parsed = parseOTPAuthURI(code.data)
        if (parsed) {
          navigate('/vault', { state: { qrData: parsed } })
          return
        }
      }
      setError('No valid QR code found in image')
    } catch (err) {
      setError('Failed to read image: ' + err.message)
    }
  }

  const startScanning = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          focusMode: 'continuous',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      })
      videoRef.current.srcObject = stream
      await videoRef.current.play()
      scanningRef.current = true
      setActive(true)
      
      const scan = async () => {
        if (!scanningRef.current || !videoRef.current) return
        const video = videoRef.current
        const canvas = canvasRef.current
        if (video.readyState < 2) {
          requestAnimationFrame(scan)
          return
        }
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        
        try {
          const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height)
          const code = await decodeFromImageData(imageData.data, imageData.width, imageData.height)
          
          if (code?.data?.startsWith('otpauth://')) {
            const parsed = parseOTPAuthURI(code.data)
            if (parsed) {
              stopScanning()
              navigate('/vault', { state: { qrData: parsed } })
              return
            }
          }
        } catch {}
        
        requestAnimationFrame(scan)
      }
      scan()
    } catch (err) {
      setError('Camera access denied: ' + err.message)
    }
  }

  useEffect(() => {
    return () => stopScanning()
  }, [])

  return (
    <div className="min-h-screen bg-vault-bg">
      <header className="bg-vault-card border-b border-gray-800 p-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => { stopScanning(); navigate('/vault') }} className="btn btn-ghost">← Back</button>
          <h1 className="text-xl font-bold">📷 Scan QR Code</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        {error && (
          <div className="bg-red-900/30 border border-red-700 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}
        
        <div className="card">
          <div className="mb-4 space-y-3">
            <button
              onClick={startScanning}
              disabled={active}
              className={`btn btn-primary w-full ${active ? 'opacity-50' : ''}`}
            >
              {active ? '📷 Scanning...' : '📷 Start Camera Scan'}
            </button>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="qr-upload"
              />
              <label htmlFor="qr-upload" className="btn btn-ghost w-full">
                📁 Upload QR Image
              </label>
            </div>
          </div>

          <div className="relative aspect-square bg-black rounded-lg overflow-hidden">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-vault-accent rounded-lg opacity-50" />
            </div>
          </div>
          
          <div className="mt-4 text-center">
            {!active ? (
              <p className="text-vault-muted">Or choose an image file with a QR code</p>
            ) : (
              <div>
                <p className="text-vault-muted mb-2">Point camera at Google Authenticator QR code</p>
                <button onClick={stopScanning} className="btn btn-ghost">Stop</button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/vault" element={<VaultListPage />} />
        <Route path="/vault/:id" element={<VaultEntryPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/qr-scan" element={<QRScannerPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    </AuthProvider>
  )
}